// TestingPageOneFile.js (fixed + smoother + App Switcher)
import React from "react";
import {
  Box,
  Flex,
  Text,
  IconButton,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
  Input,
  Button,
  ButtonGroup,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  HStack,
  Badge,
  Menu as CMenu,
  MenuButton,
  MenuList,
  MenuItem,
  useToken,
  Image,
  Icon,
} from "@chakra-ui/react";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import {
  FiAperture,
  FiSearch,
  FiMoreHorizontal,
  FiX,
  FiGrid,
  FiLayers,
} from "react-icons/fi";
import { MinusIcon } from "@chakra-ui/icons";
import TreatmentPlanListNew from "../treatmentplans/list/TreatmentPlanListNew";
import dentacasaIconNew from "../../../assets/icons/dentacasa-icon-new.svg";
import EditTreatmentPlanNewNew from "../treatmentplans/sidepanels/EditTreatmentPlanNewNew";
import { useSelector } from "react-redux";
import { textPrimary, textSecondary, white } from "../../../utils/colors";
import DashboardNew from "../dashboard/DashboardNew";
import ChatBot from "../../../components/chatbot/Chatbot";
import PatientListNewNew from "../patientsNew/list/PatientListNewNew";
import PatientSingleNew from "../patientsNew/single/PatientSingleNew";
import warm from "../../../assets/images/warm.png";
import CenterClock from "./CenterClock";
import { FaPlus, FaXmark } from "react-icons/fa6";
import CenterWelcome from "./CenterWelcome";
import Calendar from "./Calendar";

/* =========================
   Tiny OS store (Context)
   ========================= */
const OSContext = React.createContext(null);

function reducer(state, action) {
  switch (action.type) {
    case "OPEN_MENU":
      return { ...state, isMenuOpen: action.value };
    case "OPEN_APP": {
      const { app, title, meta, parentApp } = action;
      const base = parentApp || app;

      // Find an existing instance: same app + same meta.id (if provided)
      const existing = state.windows.find((w) => {
        const sameApp = w.app === app;
        const hasId = meta && meta.id != null;
        if (hasId) return sameApp && w.meta && w.meta.id === meta.id;
        // list/root apps are unique per app
        return sameApp && (!w.meta || w.meta.id == null);
      });

      if (existing) {
        return {
          ...state,
          windows: state.windows.map((w) =>
            w.id === existing.id ? { ...w, minimized: false } : w
          ),
          activeWindowId: existing.id,
        };
      }

      const newWin = {
        id: crypto.randomUUID(),
        app,
        parentApp: base,
        title,
        meta: meta || {},
        createdAt: Date.now(),
        minimized: false,
      };
      return {
        ...state,
        windows: [...state.windows, newWin],
        activeWindowId: newWin.id,
      };
    }

    case "ACTIVATE_WINDOW":
      return {
        ...state,
        activeWindowId: action.id,
      };
    case "MINIMIZE_WINDOW":
      return {
        ...state,
        windows: state.windows.map((w) =>
          w.id === action.id ? { ...w, minimized: !w.minimized } : w
        ),
        activeWindowId:
          state.activeWindowId === action.id ? null : state.activeWindowId,
      };
    case "CLOSE_WINDOW":
      return {
        ...state,
        windows: state.windows.filter((w) => w.id !== action.id),
        activeWindowId:
          state.activeWindowId === action.id ? null : state.activeWindowId,
      };

    // 🔽 App Switcher actions
    case "OPEN_SWITCHER":
      return { ...state, isSwitcherOpen: true, switcherIndex: 0 };
    case "CLOSE_SWITCHER":
      return { ...state, isSwitcherOpen: false };
    case "SET_SWITCHER_INDEX":
      return { ...state, switcherIndex: action.index };

    default:
      return state;
  }
}

function useOS() {
  const ctx = React.useContext(OSContext);
  if (!ctx) throw new Error("useOS must be used inside OSProvider");
  return ctx;
}

function OSProvider({ children }) {
  const [state, dispatch] = React.useReducer(reducer, {
    isMenuOpen: false,
    windows: [],
    activeWindowId: null,
    // switcher state
    isSwitcherOpen: false,
    switcherIndex: 0,
  });

  const openMenu = (v) => dispatch({ type: "OPEN_MENU", value: v });
  const openApp = (app, title, meta, parentApp) =>
    dispatch({ type: "OPEN_APP", app, title, meta, parentApp });
  const closeWin = (id) => dispatch({ type: "CLOSE_WINDOW", id });
  const openSwitcher = () => dispatch({ type: "OPEN_SWITCHER" });
  const closeSwitcher = () => dispatch({ type: "CLOSE_SWITCHER" });

  const value = {
    ...state,
    openMenu,
    openApp,
    closeWin,
    openSwitcher,
    closeSwitcher,
    dispatch,
  };
  return <OSContext.Provider value={value}>{children}</OSContext.Provider>;
}

/* =========================
   Shell + Wallpaper + Orb
   ========================= */
const MotionBox = motion(Box);
const EASE = [0.2, 0.8, 0.2, 1];

function Wallpaper() {
  return (
    <MotionBox
      position="absolute"
      inset="0px"
      rounded="3xl"
      overflow="hidden"
      initial={{ opacity: 0.6, scale: 1.01 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: EASE }}
      sx={{
        bgImage: warm,
        bgSize: "cover",
        bgPos: "center",
        boxShadow: "0 20px 60px rgba(0,0,0,.18)",
        willChange: "transform, opacity",
      }}
    />
  );
}

function TopOrb() {
  const { openMenu } = useOS();
  return (
    <>
      <Flex
        align="center"
        justify="center"
        position="absolute"
        bg="white"
        borderRadius="full"
        top="12px"
        left="50%"
        w="38px"
        h="38px"
        transform="translateX(-50%)"
        boxShadow="0 8px 24px rgba(0,0,0,.15)"
        onClick={() => openMenu(true)}
        transition="all 0.1s"
        zIndex={4}
        _hover={{
          transform: "scale(1.05) translateX(-50%)",
          boxShadow: "0 12px 32px rgba(0,0,0,.18)",
          cursor: "pointer",
        }}
      >
        <Image src={dentacasaIconNew} alt="Dentacasa Icon" boxSize="20px" />
      </Flex>
      {/* <IconButton
      aria-label="Menu"
      icon={<FiAperture />}
      onClick={() => openMenu(true)}
      position="absolute"
      top="10px"
      left="calc(50% - 20px)"
      rounded="full"
      size="sm"
      bg="white"
      color="purple.600"
      _hover={{ transform: "translateY(-1px)" }}
      boxShadow="0 8px 24px rgba(0,0,0,.15)"
      zIndex={4}
    /> */}
    </>
  );
}

/* =========================
   Menu Overlay
   ========================= */
function MenuOverlay() {
  const { isMenuOpen, openMenu, openApp } = useOS();

  const apps = [
    { label: "Patients", app: "patients" },
    { label: "Treatment plans", app: "treatmentPlans" },
    { label: "Dashboard", app: "dashboard" },
    { label: "Employees", app: "employees" },
    { label: "Outreach", app: "outreach" },
    { label: "Settings", app: "settings" },
  ];

  const Card = ({ label, onClick }) => (
    <Flex
      onClick={onClick}
      align="center"
      justify="space-between"
      bg="whiteAlpha.700"
      _hover={{ bg: "white", transform: "translateY(-2px)" }}
      rounded="xl"
      p={5}
      cursor="pointer"
      backdropFilter="blur(8px)"
      transition="all .2s"
    >
      <Text fontWeight="600">{label}</Text>
      <Box w="10px" h="10px" bg="gray.200" rounded="full" />
    </Flex>
  );

  return (
    <AnimatePresence>
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            backdropFilter: "blur(12px)",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 3,
          }}
          onClick={() => openMenu(false)}
        >
          <motion.div
            initial={{ y: 22, scale: 0.985 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 10, opacity: 0.9 }}
            transition={{
              type: "spring",
              stiffness: 180,
              damping: 22,
              mass: 0.8,
            }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 780,
              background: "rgba(255,255,255,0.7)",
              borderRadius: 20,
              padding: 24,
              boxShadow: "0 30px 80px rgba(0,0,0,.20)",
              willChange: "transform, opacity",
            }}
          >
            <InputGroup mb={5}>
              <InputLeftElement pointerEvents="none">
                <FiSearch />
              </InputLeftElement>
              <Input placeholder="Search" rounded="full" bg="white" />
            </InputGroup>

            <SimpleGrid columns={2} spacing={4}>
              {apps.map((a) => (
                <Card
                  key={a.app}
                  label={a.label}
                  onClick={() => {
                    openApp(a.app, a.label, undefined, a.app);
                    setTimeout(() => openMenu(false), 0);
                  }}
                />
              ))}
            </SimpleGrid>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* =========================
   App Switcher Overlay (CMD+Tab style)
   ========================= */
function AppSwitcherOverlay() {
  const { windows, isSwitcherOpen, switcherIndex, dispatch, closeSwitcher } =
    useOS();
  const [purple600] = useToken("colors", ["purple.600"]);

  // Deduplicate by app, keep the most recent window per app
  const entries = React.useMemo(() => {
    const map = new Map();
    for (let i = windows.length - 1; i >= 0; i--) {
      const w = windows[i];
      const key = `${w.app}-${w.meta?.id ?? "root"}`; // root for list windows
      if (!map.has(key)) map.set(key, w);
    }
    return Array.from(map.values());
  }, [windows]);

  const selectIndex = (idx) =>
    dispatch({ type: "SET_SWITCHER_INDEX", index: idx });

  const activateSelected = () => {
    const item = entries[switcherIndex];
    if (!item) return;
    // unminimize if needed
    if (item.minimized) {
      dispatch({ type: "MINIMIZE_WINDOW", id: item.id });
    }
    dispatch({ type: "ACTIVATE_WINDOW", id: item.id });
    closeSwitcher();
  };

  React.useEffect(() => {
    if (!isSwitcherOpen) return;
    const onKey = (e) => {
      // prevent tab from moving focus out of overlay
      if (
        ["Tab", "ArrowRight", "ArrowLeft", "Enter", "Escape"].includes(e.key)
      ) {
        e.preventDefault();
      }
      if (e.key === "Escape") return closeSwitcher();
      if (e.key === "Enter") return activateSelected();

      const max = entries.length;
      if (!max) return;

      const next = () => selectIndex((switcherIndex + 1) % max);
      const prev = () => selectIndex((switcherIndex - 1 + max) % max);

      if (e.key === "Tab") {
        if (e.shiftKey) prev();
        else next();
      } else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () =>
      window.removeEventListener("keydown", onKey, { capture: true });
  }, [isSwitcherOpen, switcherIndex, entries.length]);

  return (
    <AnimatePresence>
      {isSwitcherOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            backdropFilter: "blur(6px)",
            zIndex: 5,
          }}
          onClick={closeSwitcher}
        >
          <motion.div
            initial={{ y: 22, scale: 0.985 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 10, opacity: 0.9 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 22,
              mass: 0.8,
            }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 760,
              background: "rgba(255,255,255,0.75)",
              borderRadius: 18,
              padding: 18,
              boxShadow: "0 30px 80px rgba(0,0,0,.24)",
            }}
          >
            <HStack mb={3} px={2} justify="space-between" align="center">
              <HStack>
                <FiLayers />
                <Text fontWeight="700">Switch apps</Text>
                <Badge rounded="full" colorScheme="purple">
                  Cmd/Ctrl + K
                </Badge>
              </HStack>
              <Text fontSize="sm" color="gray.500">
                Tab / ← → to cycle • Enter to switch • Esc to close
              </Text>
            </HStack>

            <SimpleGrid columns={4} spacing={3}>
              {entries.length === 0 && (
                <Box p={8} textAlign="center">
                  <Text color="gray.500">No apps open yet.</Text>
                </Box>
              )}
              {entries.map((w, idx) => (
                <Flex
                  key={w.id}
                  role="button"
                  onClick={() => {
                    selectIndex(idx);
                    activateSelected();
                  }}
                  direction="column"
                  align="center"
                  justify="center"
                  rounded="xl"
                  p={5}
                  gap={3}
                  bg={idx === switcherIndex ? "white" : "whiteAlpha.700"}
                  boxShadow={idx === switcherIndex ? "lg" : "sm"}
                  border={
                    idx === switcherIndex
                      ? `2px solid ${purple600}`
                      : "1px solid rgba(0,0,0,.08)"
                  }
                  transition="all .15s"
                  cursor="pointer"
                >
                  <Flex
                    align="center"
                    justify="center"
                    w="64px"
                    h="64px"
                    rounded="2xl"
                    bg="blackAlpha.100"
                  >
                    <FiGrid size={28} />
                  </Flex>
                  <Text fontWeight="600" textAlign="center">
                    {w.title}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    {w.app}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* =========================
   Window + Manager
   ========================= */
function TabBar() {
  const { windows, activeWindowId, closeWin, dispatch, openApp } = useOS();

  // group by base (parentApp || app)
  const groups = React.useMemo(() => {
    const map = new Map();
    for (const w of windows) {
      const base = w.parentApp || w.app;
      if (!map.has(base)) map.set(base, []);
      map.get(base).push(w);
    }
    return map;
  }, [windows]);

  const activateOrUnminimize = (id) => {
    const win = windows.find((w) => w.id === id);
    if (!win) return;
    if (win.minimized) dispatch({ type: "MINIMIZE_WINDOW", id });
    dispatch({ type: "ACTIVATE_WINDOW", id });
  };

  const toggleOrActivate = (id) => {
    const win = windows.find((w) => w.id === id);
    if (!win) return;

    // If it's the active one → minimize it
    if (activeWindowId === id) {
      dispatch({ type: "MINIMIZE_WINDOW", id });
      return;
    }

    // If it's minimized → unminimize, then activate
    if (win.minimized) {
      dispatch({ type: "MINIMIZE_WINDOW", id }); // toggles to unminimized
      dispatch({ type: "ACTIVATE_WINDOW", id });
      return;
    }

    // Otherwise → just activate
    dispatch({ type: "ACTIVATE_WINDOW", id });
  };

  const ParentTab = ({ base, listWin }) => {
    const label =
      base === "patients"
        ? "Patients"
        : base === "treatmentPlans"
        ? "Treatment Plans"
        : listWin?.title || base;

    const handleClick = () => {
      if (listWin) toggleOrActivate(listWin.id);
      else {
        if (base === "patients") openApp("patients", "Patients");
        else if (base === "treatmentPlans")
          openApp("treatmentPlans", "Treatment Plans");
      }
    };

    return (
      <Flex
        align="center"
        px={4}
        py={1}
        bg={
          listWin && listWin.id === activeWindowId ? "white" : "whiteAlpha.400"
        }
        borderRadius="100px"
        boxShadow={listWin && listWin.id === activeWindowId ? "sm" : "none"}
        cursor="pointer"
        onClick={handleClick}

        // onClick={() => {
        //   if (listWin) activateOrUnminimize(listWin.id);
        //   else {
        //     // If list not open yet, open it
        //     if (base === "patients") openApp("patients", "Patients");
        //     else if (base === "treatmentPlans")
        //       openApp("treatmentPlans", "Treatment Plans");
        //   }
        // }}
      >
        <Text fontSize="sm" fontWeight="500">
          {label}
        </Text>

        <IconButton
          aria-label="Close"
          icon={<FiX />}
          size="xs"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            closeWin(listWin.id);
          }}
          ml={2}
        />
      </Flex>
    );
  };

  const Chip = ({ w }) => {
    const label = w?.meta?.treatmentPlanID
      ? `Plan ${w.meta.treatmentPlanID}`
      : w.title;

    return (
      <HStack
        key={w.id}
        spacing={1}
        pl={2}
        pr={1}
        py={1}
        rounded="full"
        bg={w.id === activeWindowId ? "white" : "whiteAlpha.700"}
        border={
          w.id === activeWindowId
            ? "1px solid rgba(0,0,0,.12)"
            : "1px solid rgba(0,0,0,.06)"
        }
        boxShadow={w.id === activeWindowId ? "sm" : "none"}
        cursor="pointer"
        onClick={() => toggleOrActivate(w.id)}
      >
        <Text fontSize="xs" fontWeight="600">
          {label} {/* <-- now shows Plan TP-010 */}
        </Text>
        {/* <IconButton
        aria-label="Minimize"
        icon={<MinusIcon />}
        size="xs"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();
          dispatch({ type: "MINIMIZE_WINDOW", id: w.id });
        }}
      /> */}
        <IconButton
          aria-label="Close"
          icon={<FiX />}
          size="xs"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            closeWin(w.id);
          }}
        />
      </HStack>
    );
  };

  // Render: patients group, treatmentPlans group, then other groups linearly
  const orderedKeys = Array.from(groups.keys()).sort((a, b) => {
    const rank = (k) => (k === "patients" ? 0 : k === "treatmentPlans" ? 1 : 2);
    return rank(a) - rank(b);
  });

  return (
    <Flex
      bg="whiteAlpha.80"
      backdropFilter="blur(6px)"
      borderBottom="1px solid rgba(0,0,0,0.08)"
      px={3}
      py={2}
      align="center"
      gap={3}
    >
      {orderedKeys.map((base) => {
        const items = groups.get(base) || [];
        const listWin = items.find((w) => w.app === base) || null;
        const detailWins = items.filter((w) => w.app !== base);

        // For non-patient/plan groups, render as simple tabs
        if (base !== "patients" && base !== "treatmentPlans") {
          return items.map((w) => (
            <Flex
              key={w.id}
              align="center"
              px={4}
              py={1}
              bg={w.id === activeWindowId ? "white" : "whiteAlpha.400"}
              borderRadius="100px"
              boxShadow={w.id === activeWindowId ? "sm" : "none"}
              cursor="pointer"
              onClick={() => toggleOrActivate(w.id)}
            >
              <Text fontSize="sm" fontWeight="500" mr={2}>
                {w.title}
              </Text>

              <IconButton
                aria-label="Close tab"
                icon={<FiX />}
                size="xs"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  closeWin(w.id);
                }}
              />
            </Flex>
          ));
        }

        // Patients / Treatment Plans: render parent + chips of details
        return (
          <HStack
            key={base}
            spacing={2}
            align="center"
            maxW="45vw"
            overflow="hidden"
          >
            <ParentTab base={base} listWin={listWin} />
            <HStack spacing={1} overflowX="auto">
              {detailWins.map((w) => (
                <Chip key={w.id} w={w} />
              ))}
            </HStack>
          </HStack>
        );
      })}
    </Flex>
  );
}

function AppWindow({ id, title, children }) {
  const { closeWin } = useOS();

  const { windows, dispatch } = useOS();
  const w = windows.find((w) => w.id === id);
  if (!w) return null; // If window not found, do not render

  return (
    <Box
      as={motion.div}
      initial={{ opacity: 0, y: 12, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 12, scale: 0.985 }}
      // transition={{ type: "spring", stiffness: 360, damping: 28, mass: 0.6 }}
      transition={{ type: "tween", duration: 0.18, ease: "easeOut" }}
      w="auto"
      maxW="1200px"
      m="0 auto"
      style={{
        background: "rgba(255,255,255,0.2)",
        backdropFilter: "blur(3px)",
        borderRadius: "1rem",
        // boxShadow: "0 30px 60px rgba(0,0,0,.18)",
        // height: "100%",
        // overflow: "hidden",
        position: "relative",
        willChange: "transform, opacity",
      }}
    >
      <Flex
        h="40px"
        align="center"
        justify="space-between"
        px="1.5rem"
        borderBottom="1px solid rgba(0,0,0,.06)"
        // background="rgba(255,255,255,0.2)"
        backdropFilter="blur(6px)"
        borderTopLeftRadius="1rem"
        borderTopRightRadius="1rem"
      >
        <Text fontWeight="600" fontSize="sm" color={textSecondary}>
          {title}
        </Text>

        <Flex align="center" justify="flex-end" gap={2}>
          <Flex
            align="center"
            justify="center"
            // bg="yellow.400"
            aria-label="Minimize"
            borderRadius="full"
            w="16px"
            h="16px"
            p="0"
            onClick={(e) => {
              e.stopPropagation();
              dispatch({ type: "MINIMIZE_WINDOW", id: w.id });
            }}
            _hover={{
              cursor: "pointer",
              bg: "rgba(0,0,0,0.1)",
            }}
          >
            <Icon fontSize="8px" as={MinusIcon} color={textSecondary} />
          </Flex>

          <Flex
            align="center"
            justify="center"
            // bg="red.400"
            aria-label="Minimize"
            borderRadius="full"
            w="16px"
            h="16px"
            p="0"
            onClick={() => closeWin(id)}
            _hover={{
              cursor: "pointer",
              bg: "rgba(0,0,0,0.1)",
            }}
          >
            <Icon fontSize="10px" as={FaXmark} color={textSecondary} />
          </Flex>
          {/* 
          <IconButton
            aria-label="Close"
            icon={<FiX />}
            size="sm"
            onClick={() => closeWin(id)}
            variant="ghost"
          /> */}
        </Flex>
      </Flex>
      <Box
        w="auto"
        p="0px"
        h="calc(100% - 56px)"
        //   overflow="auto"
      >
        {children}
      </Box>
    </Box>
  );
}

function CalendarApp() {
  const { openApp } = useOS();

  return (
    <div className="h-screen">
      <Calendar />
    </div>
  );
}

function PatientsApp() {
  const { openApp } = useOS();

  return (
    <Box>
      <PatientListNewNew
        onOpenPatient={(meta) =>
          openApp(
            "patientDetail",
            meta?.name || `Patient ${meta?.id}`,
            meta,
            "patients"
          )
        }
      />
    </Box>
  );
}

function DashboardApp() {
  const { openApp } = useOS();

  return (
    <Box p="1.5rem 1.5rem 0" h="100%">
      <DashboardNew />
    </Box>
  );
}

function TreatmentPlansApp() {
  const { openApp } = useOS();
  const plans = Array.from({ length: 10 }).map((_, i) => ({
    id: `TP-${String(i + 1).padStart(3, "0")}`,
    patient: "Pacientauskas Testas",
    services: `${8 + (i % 3)}/18`,
    price: `€ ${7850 - i * 50}`,
    created: "December 13",
    status: ["Planning", "Approved", "In progress"][i % 3],
  }));
  return (
    <Box w="100%" h="100%">
      <TreatmentPlanListNew
        p="1rem"
        pt="0"
        minH="100%"
        onOpenPlan={(meta) => {
          const humanId = meta?.treatmentPlanID || meta?.id || "—";
          openApp(
            "treatmentPlanDetail",
            `Plan ${humanId}`, // <-- prefer TP-010 style
            meta,
            "treatmentPlans"
          );
        }}
      />
    </Box>
  );
}

function PatientDetailApp({ meta }) {
  const data = meta || {};

  console.log("PatientDetailApp meta:", meta);

  return (
    <Box>
      <PatientSingleNew patientId={data?._id || data?.patientId || data?.id} />
    </Box>
  );
}

function TreatmentPlanDetailApp({ meta }) {
  // ✅ Hooks must be called unconditionally, at the top
  const { userInfo } = useSelector((s) => s.authLogin || {});
  const { tenant } = useSelector((s) => s.tenantDetailsBySlug || {});

  // You can safely compute from meta (meta might be undefined)
  const planId = meta?._id || meta?.planId || meta?.id;

  // Permissions (defensively handle missing)
  const currentTenantSlug = tenant?.slug;
  const currentTenantAccess =
    userInfo?.tenants?.find((t) => t.slug === currentTenantSlug) || {};
  const perms = currentTenantAccess.permissions || {};

  const hasAccessToUpdate = !!perms?.treatmentPlans?.update;
  const hasAccessToPatientSensitiveData = !!perms?.patients?.sensitiveData;
  const hasAccessSendToPatient = !!perms?.treatmentPlans?.sendToPatient;

  // If we still don't have an id to edit, render a lightweight placeholder
  if (!planId) {
    return <Text px={4}>Loading plan…</Text>;
  }

  return (
    <Flex m="0 auto" w="800px" position="relative">
      {/* <HStack mb={4} spacing={3}>
        <FiGrid />
        <Text fontWeight="700">Treatment Plan {meta?.id}</Text>
        {meta?.status && (
          <Badge colorScheme="purple" rounded="full">
            {meta.status}
          </Badge>
        )}
      </HStack> */}

      <EditTreatmentPlanNewNew
        hasEditAccess={hasAccessToUpdate}
        hasAccessToPatientSensitiveData={hasAccessToPatientSensitiveData}
        hasAccessSendToPatient={hasAccessSendToPatient}
        planId={planId}
        currentUser={userInfo}
        currency={tenant && tenant.currency}
        close={() => {}}
      />
    </Flex>
  );
}

const appMap = {
  patients: PatientsApp,
  treatmentPlans: TreatmentPlansApp,
  patientDetail: PatientDetailApp,
  treatmentPlanDetail: TreatmentPlanDetailApp,
  dashboard: DashboardApp,
  employees: () => (
    <Box p={6}>
      <Text fontWeight="700">Employees</Text>
    </Box>
  ),
  outreach: CalendarApp,
  settings: () => (
    <Box p={6}>
      <Text fontWeight="700">Settings</Text>
    </Box>
  ),
};

function WindowManager() {
  const { windows, activeWindowId } = useOS();
  const activeWindow = windows.find(
    (w) => w.id === activeWindowId && !w.minimized
  );
  const AppComp = activeWindow ? appMap[activeWindow.app] : null;

  return (
    <Box
      position="absolute"
      inset="0"
      p="0px"
      pt="96px"
      zIndex={1}
      display="flex"
      flexDir="column"
      h="100%"
      overflow="scroll"
    >
      <Box h="100%" flex="1">
        <AnimatePresence mode="wait">
          {activeWindow && AppComp && (
            <AppWindow
              key={activeWindow.id}
              id={activeWindow.id}
              title={
                activeWindow?.meta?.treatmentPlanID
                  ? `Plan ${activeWindow.meta.treatmentPlanID}`
                  : activeWindow.title
              }
            >
              {/* ✅ pass meta down */}
              <AppComp meta={activeWindow.meta} />
            </AppWindow>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
}

/* =========================
   Floating Action Bar
   ========================= */
function FAB() {
  const { windows, openMenu } = useOS();
  const active = windows.at(-1)?.app;

  const actionsByApp = {
    patients: [{ label: "New patient", onClick: () => {} }],
    treatmentPlans: [
      { label: "Create plan", onClick: () => {} },
      { label: "Filter", onClick: () => {} },
    ],
    default: [{ label: "Open menu", onClick: () => openMenu(true) }],
  };
  const actions = actionsByApp[active] || actionsByApp.default;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.25, ease: EASE }}
        style={{
          position: "absolute",
          left: "50%",
          bottom: 18,
          transform: "translateX(-50%)",
          zIndex: 2,
          willChange: "transform, opacity",
        }}
      >
        <Box
          bg="white"
          rounded="full"
          p="6px"
          boxShadow="0 6px 30px rgba(0,0,0,.18)"
        >
          <ButtonGroup isAttached>
            {actions.map((a, i) => (
              <Button key={i} onClick={a.onClick} variant="ghost">
                {a.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      </motion.div>
    </AnimatePresence>
  );
}

/* =========================
   Page (single-file shell)
   ========================= */
function ShellInner({ children }) {
  const { openApp, openMenu, openSwitcher, windows, activeWindowId, dispatch } =
    useOS();

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key.toLowerCase() === "m") openMenu(true);

      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        openSwitcher();
        return;
      }

      if (isMod && (e.key === "m" || e.key === "M")) {
        e.preventDefault();
        if (activeWindowId) {
          dispatch({ type: "MINIMIZE_WINDOW", id: activeWindowId });
        }
        return;
      }

      const active = windows.find((w) => w.id === activeWindowId);
      if (!active) return;

      const base = active.parentApp || active.app;
      const isPatientDetail = active.app === "patientDetail";
      const isPlanDetail = active.app === "treatmentPlanDetail";

      if (
        (isPatientDetail || isPlanDetail) &&
        (e.key === "ArrowLeft" || e.key === "ArrowRight")
      ) {
        e.preventDefault();
        const groupDetails = windows.filter(
          (w) => (w.parentApp || w.app) === base && w.app !== base
        );
        if (groupDetails.length < 2) return;

        const idx = groupDetails.findIndex((w) => w.id === active.id);
        const nextIdx =
          e.key === "ArrowRight"
            ? (idx + 1) % groupDetails.length
            : (idx - 1 + groupDetails.length) % groupDetails.length;

        const target = groupDetails[nextIdx];
        if (target.minimized)
          dispatch({ type: "MINIMIZE_WINDOW", id: target.id });
        dispatch({ type: "ACTIVATE_WINDOW", id: target.id });
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openMenu, openSwitcher, windows, activeWindowId, dispatch]);

  return (
    <>
      <Box
        position="absolute"
        inset="32px"
        rounded="3xl"
        overflow="hidden"
        zIndex={0}
      >
        <Wallpaper />
      </Box>
      <TopOrb />
      <Box
        position="absolute"
        inset="43px 60px 32px"
        rounded="3xl"
        // overflow="scroll"
        zIndex={1}
      >
        {children}
      </Box>

      {/* // BIG APPLE STYLE TIME OF THE DAY, WHAT DAY IT IS OF THE WEEK, ETC */}

      {/* BIG APPLE STYLE TIME OF THE DAY */}
      <AnimatePresence>
        {windows.length === 0 && (
          <motion.div
            key="center-clock"
            initial={{ opacity: 0, y: 6, scale: 0.995 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.995 }}
            transition={{ type: "tween", duration: 0.18, ease: "easeOut" }}
            // style={{
            //   position: "absolute",
            //   top: "50%",
            //   left: "50%",
            //   transform: "translate(-50%, -50%)",
            // }}
          >
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
            >
              <CenterClock />
              {/* <CenterWelcome /> */}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating TabBar */}
      <Box
        position="absolute"
        top="42px"
        left="16px"
        right="16px"
        w="90%"
        m="0 auto"
        borderRadius="100px"
        overflow="hidden"
        zIndex={2}
      >
        {windows.length > 0 && <TabBar />}
      </Box>

      <MenuOverlay />
      <AppSwitcherOverlay />
      <ChatBot
        onOpenPatient={(meta) =>
          openApp(
            "patientDetail",
            meta?.name || `Patient ${meta?.id}`,
            meta,
            "patients"
          )
        }
      />
      {/* <FAB /> */}
    </>
  );
}

export default function TestingPage() {
  return (
    <MotionConfig
      // transition={{ type: "spring", stiffness: 170, damping: 20, mass: 0.8 }}]

      transition={{ type: "spring", stiffness: 320, damping: 26, mass: 0.7 }}
      reducedMotion="user"
    >
      <OSProvider>
        {/* 👇 give the shell actual height so absolute children can fill it */}
        <Box position="relative" w="100%" h="100vh" bg="#F2F2F2">
          <ShellInner>
            <WindowManager />
          </ShellInner>
        </Box>
      </OSProvider>
    </MotionConfig>
  );
}
